import { useMutation } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useDispatch, useStore } from 'react-redux'

import { authAPI } from '@/api/new-api'
import { UPDATE_BUILDING } from '@/reducers/buildingSelectorReducer'
import { Building } from '@/reducers/types'

type LuxerLocation = { id: number; luxerOneId?: number | string; [key: string]: unknown }

type BuildingStore = { buildingSelector: { buildings: Building[] } }

// Note: the building list lives in Redux, so a change to a building's own data is written back there
const useReplaceStoredBuilding = () => {
  const dispatch = useDispatch()
  const store = useStore<BuildingStore>()

  return useCallback(
    (buildingId: number, change: (stored: Building) => Building) => {
      const stored = store
        .getState()
        .buildingSelector.buildings.find((b: Building) => b.id === buildingId)
      if (!stored) return
      dispatch({ buildingData: change(stored), type: UPDATE_BUILDING })
    },
    [dispatch, store],
  )
}

export interface AddLuxerRequest {
  buildingId: number
  luxerOneId: number
}

export const addLuxerLocation = async ({ buildingId, luxerOneId }: AddLuxerRequest) => {
  const response = await authAPI.post<LuxerLocation>('/luxer', { building: buildingId, luxerOneId })
  return response.data
}

export const useAddLuxerLocation = () => {
  const replaceStoredBuilding = useReplaceStoredBuilding()

  return useMutation({
    mutationFn: addLuxerLocation,
    onSuccess: (location, { buildingId }) =>
      replaceStoredBuilding(buildingId, (stored) => ({
        ...stored,
        luxerOneLocations: [...(stored.luxerOneLocations ?? []), location],
      })),
  })
}

export interface RemoveLuxerRequest {
  buildingId: number
  luxerId: number
}

export const removeLuxerLocation = async ({ luxerId }: RemoveLuxerRequest) => {
  await authAPI.delete(`/luxer/${luxerId}`)
}

export const useRemoveLuxerLocation = () => {
  const replaceStoredBuilding = useReplaceStoredBuilding()

  return useMutation({
    mutationFn: removeLuxerLocation,
    onSuccess: (_, { buildingId, luxerId }) =>
      replaceStoredBuilding(buildingId, (stored) => ({
        ...stored,
        luxerOneLocations: (stored.luxerOneLocations ?? []).filter(({ id }) => id !== luxerId),
      })),
  })
}

export interface UpdateApartmentsIdRequest {
  buildingId: number
  locationId: string
}

export const updateApartmentsId = async ({ buildingId, locationId }: UpdateApartmentsIdRequest) => {
  const response = await authAPI.put<Building>(`/apartments/${buildingId}`, { locationId })
  return response.data
}

export const useUpdateApartmentsId = () => {
  const replaceStoredBuilding = useReplaceStoredBuilding()

  return useMutation({
    mutationFn: updateApartmentsId,
    onSuccess: (building, { buildingId }) =>
      replaceStoredBuilding(buildingId, (stored) => ({ ...stored, ...building })),
  })
}

export const syncSaltoSpaceAccessPoints = async (buildingId: number) => {
  await authAPI.post(`/salto-space/access-points/${buildingId}/sync`, {})
}

export const useSyncSaltoSpaceAccessPoints = () =>
  useMutation({ mutationFn: syncSaltoSpaceAccessPoints })
